import React from 'react';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { BiDotsVerticalRounded, BiFingerprint, BiUser, BiLinkExternal } from 'react-icons/bi';
import {
  OrderActionMenu,
  OrderTooltip,
  ORDER_OFFER_STATUS_LABELS as OFFER_STATUS_FILTER_LABELS,
  type OrderMenuAction,
  type OrderOfferStatusKey as OfferStatusFilterKey,
} from '@hopdrive/feature-order-index';
import { formatOfferActor, formatOfferTime } from '@features/moves/utils/moveDetailsAssignment';
import { buildStatusTooltip, formatTimeToAccept, type ViewAssignmentsRow } from '../utils/viewAssignmentsRows';

/** Status chip tone — mirrors the offered/accepted tones the pill and panel use; unassigned is neutral. */
const STATUS_TONE: Record<OfferStatusFilterKey, 'default' | 'info' | 'success'> = {
  unassigned: 'default',
  offered: 'info',
  accepted: 'success',
};

/** Lifecycle order for sorting the Status column — never alphabetical. */
const STATUS_ORDER: Record<OfferStatusFilterKey, number> = { unassigned: 0, offered: 1, accepted: 2 };

const toMs = (at: string | null): number => (at ? new Date(at).getTime() : 0);

type SortKey = 'order' | 'guest' | 'driver' | 'status' | 'offered' | 'accepted' | 'timeToAccept';

interface ColumnDef {
  key: SortKey;
  label: string;
  sortValue: (row: ViewAssignmentsRow) => number | string;
  render: (row: ViewAssignmentsRow) => React.ReactNode;
}

const COLUMNS: ColumnDef[] = [
  {
    key: 'order',
    label: 'Order',
    sortValue: row => row.driveMoveIds[0] ?? 0,
    render: row => (
      <Box>
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          {row.displayName}
        </Typography>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px', color: 'text.secondary' }}>
          <BiFingerprint size={10} aria-hidden style={{ flex: 'none' }} />
          <Typography variant='caption'>{row.driveMoveIds.join(' · ')}</Typography>
        </Box>
      </Box>
    ),
  },
  {
    key: 'driver',
    label: 'Driver',
    sortValue: row => row.driver?.display_name ?? '',
    render: row =>
      row.driver ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar src={row.driver.avatar_url || ''} alt={row.driver.display_name || ''} sx={{ width: 28, height: 28 }} />
          <Typography variant='body2'>{row.driver.display_name || '—'}</Typography>
        </Box>
      ) : (
        <Typography variant='body2' color='text.secondary'>
          —
        </Typography>
      ),
  },
  {
    key: 'status',
    label: 'Status',
    sortValue: row => STATUS_ORDER[row.status],
    render: row => (
      <OrderTooltip content={buildStatusTooltip(row)} placement='top'>
        <Chip size='small' color={STATUS_TONE[row.status]} label={OFFER_STATUS_FILTER_LABELS[row.status]} />
      </OrderTooltip>
    ),
  },
  {
    key: 'offered',
    label: 'Offered',
    sortValue: row => toMs(row.offeredAt),
    render: row =>
      row.offeredAt ? (
        <Box>
          <Typography variant='body2'>{formatOfferTime(row.offeredAt)}</Typography>
          {row.offeredBy && formatOfferActor(row.offeredBy) !== '—' && (
            <Typography variant='caption' color='text.secondary'>
              {formatOfferActor(row.offeredBy)}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography variant='body2' color='text.secondary'>
          —
        </Typography>
      ),
  },
  {
    key: 'accepted',
    label: 'Accepted',
    sortValue: row => toMs(row.acceptedAt),
    render: row =>
      row.acceptedAt ? (
        <Box>
          <Typography variant='body2'>{formatOfferTime(row.acceptedAt)}</Typography>
          {row.acceptedBy && formatOfferActor(row.acceptedBy) !== '—' && (
            <Typography variant='caption' color='text.secondary'>
              {formatOfferActor(row.acceptedBy)}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography variant='body2' color='text.secondary'>
          —
        </Typography>
      ),
  },
  {
    key: 'timeToAccept',
    label: 'Time to accept',
    sortValue: row => row.timeToAcceptMs ?? -1,
    render: row => <Typography variant='body2'>{formatTimeToAccept(row.timeToAcceptMs)}</Typography>,
  },
  {
    key: 'guest',
    label: 'Guest',
    sortValue: row => row.consumerName ?? '',
    render: row =>
      row.consumerName ? (
        <Typography variant='body2'>{row.consumerName}</Typography>
      ) : (
        <Typography variant='body2' color='text.secondary'>
          —
        </Typography>
      ),
  },
];

export interface ViewAssignmentsTableProps {
  rows: ViewAssignmentsRow[];
  /** Called when the user selects "View move details" for a row. */
  onViewDetails: (row: ViewAssignmentsRow) => void;
  /** Called when the user selects "Assign/Reassign driver" for a row. */
  onAssignDriver: (row: ViewAssignmentsRow) => void;
}

/**
 * Build the 3-dot menu actions for a row.
 *
 * `row.status` picks the WORD — Assign for an order with no driver, Reassign for one that
 * has one. It says nothing about whether the action is legal: an Accepted row can be a
 * delivered move. Legality is `row.assignable`, the same `canAssignOrUnassignMove` verdict
 * the move details panel greys its own button on, so the two surfaces refuse together.
 * Disabled rather than hidden, with the reason on hover: a dispatcher who cannot find the
 * action has no way to learn it was withheld on purpose.
 */
const buildRowActions = (
  row: ViewAssignmentsRow,
  onViewDetails: (row: ViewAssignmentsRow) => void,
  onAssignDriver: (row: ViewAssignmentsRow) => void
): OrderMenuAction[] => {
  const hasDriver = row.status !== 'unassigned';
  return [
    {
      label: 'View move details',
      icon: <BiLinkExternal size={16} />,
      handler: () => onViewDetails(row),
    },
    {
      label: hasDriver ? 'Reassign driver' : 'Assign driver',
      icon: <BiUser size={16} />,
      disabled: !row.assignable,
      disabledReason: row.assignBlockedReason ?? undefined,
      handler: () => onAssignDriver(row),
    },
  ];
};

const ViewAssignmentsTable: React.FC<ViewAssignmentsTableProps> = ({ rows, onViewDetails, onAssignDriver }) => {
  const [sortKey, setSortKey] = React.useState<SortKey>('order');
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc');

  const sorted = React.useMemo(() => {
    const column = COLUMNS.find(c => c.key === sortKey);
    if (!column) return rows;
    const factor = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = column.sortValue(a);
      const bv = column.sortValue(b);
      if (av < bv) return -1 * factor;
      if (av > bv) return 1 * factor;
      // Stable tiebreak on scenario id so equal sort values never reshuffle between
      // renders. Deliberately NOT multiplied by `factor`: ties keep their natural
      // id order in both directions, so flipping the sort doesn't also reorder rows
      // the dealer sees as equal (this table gets read side by side and exported).
      return a.scenarioId - b.scenarioId;
    });
  }, [rows, sortKey, sortDir]);

  const onSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(dir => (dir === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size='small'>
        <TableHead>
          <TableRow>
            {COLUMNS.map(column => (
              <TableCell key={column.key} sortDirection={sortKey === column.key ? sortDir : false}>
                <TableSortLabel
                  active={sortKey === column.key}
                  direction={sortKey === column.key ? sortDir : 'asc'}
                  onClick={() => onSort(column.key)}
                >
                  {column.label}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell sx={{ width: 48 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map(row => (
            <TableRow key={row.scenarioId} hover>
              {COLUMNS.map(column => (
                <TableCell key={column.key}>{column.render(row)}</TableCell>
              ))}
              <TableCell sx={{ width: 48, p: 0 }}>
                <OrderActionMenu actions={buildRowActions(row, onViewDetails, onAssignDriver)}>
                  {({ onClick, isOpen }) => (
                    <IconButton
                      size='small'
                      onClick={onClick}
                      sx={{ color: isOpen ? 'primary.main' : 'text.secondary' }}
                    >
                      <BiDotsVerticalRounded size={18} />
                    </IconButton>
                  )}
                </OrderActionMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ViewAssignmentsTable;
